/**
 * form-props.ts — the property-not-attribute rule for form-control props,
 * shared by every compiled writer (BUG-RUNTIME-130).
 *
 * ATLAS-DOMWRITE-010: React writes `value` / `defaultValue` / `defaultChecked`
 * as DOM PROPERTIES, never as attributes. `setAttribute('defaultValue', x)`
 * produces a dead lowercase `defaultvalue="x"` attribute that no property ever
 * reads, and the field renders EMPTY - a user-visible defect, not a markup nit
 * (BUG-RUNTIME-076, BUG-RUNTIME-065).
 *
 * It lives in its own leaf module because the rule had TWO writers on the
 * compiled path and only one of them ever learned it:
 *
 *   - `writePropOnce` (compiled/props.ts) - the SPREAD writer, fixed by 076;
 *   - `_applyHostAttr` (compiled/render-component.ts) - the writer for a host
 *     element whose tag is only known at runtime (`asChild ? Slot : 'button'`)
 *     and whose props arrive as an opaque bag, which never was.
 *
 * BUG-RUNTIME-130's ticket names the hazard directly: *a fix that duplicates
 * the table rather than sharing it will diverge again the next time the table
 * moves.* So neither writer owns the rule now; both ask this.
 *
 * The gate React applies is PRESENCE, not truthiness: `initInput` opens with
 * `if (null != value || null != defaultValue)`, so `defaultValue=""` is inside
 * the gate and writes `element.defaultValue = ''`, which materialises
 * `value=""` (ATLAS-DOMWRITE-002/003). A nullish one is outside it and writes
 * nothing at all.
 */
/**
 * Write `key` if it is one of the form-control props whose home is a DOM
 * property. Returns true when this module has handled the key - including the
 * cases where the correct action is to write NOTHING - and false when the
 * caller should fall through to its own generic path.
 *
 * Deliberately NOT handled here: `checked`, `disabled`, `selected` and
 * `htmlFor`. Each already has per-writer handling whose observable differs
 * (attribute + property vs property alone), and unifying those is a separate
 * measurement, not this fix.
 */
export function writeFormControlProp(el, key, value) {
    if (key !== 'value' && key !== 'defaultValue' && key !== 'defaultChecked')
        return false;
    const tag = el.tagName;
    const isFormControl = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    if (key === 'value') {
        // On anything else `value` is an ordinary attribute React serializes with
        // String(value) - `<option value>`, `<li value>`, `<progress value>` - so
        // the caller's generic path is correct there and this one is not.
        if (!isFormControl)
            return false;
        // React never writes a null/undefined value: `value == null` means the
        // control is UNCONTROLLED (initInput's `null != value` guard). Assigning it
        // would stringify to "undefined".
        if (value == null)
            return true;
        el.value = value;
        // React reflects the controlled value into the `value` ATTRIBUTE by
        // assigning the defaultValue IDL property (initInput's
        // `element.defaultValue = value`, setDefaultValue on update). The one
        // exception is a focused number input, which React leaves alone so the
        // attribute cannot fight the digits being typed.
        if ((tag === 'INPUT' || tag === 'TEXTAREA') &&
            !(el.type === 'number' && typeof document !== 'undefined' && document.activeElement === el)) {
            el.defaultValue = value;
        }
        return true;
    }
    if (key === 'defaultValue') {
        // `defaultValue` is a RESERVED prop on every element: react-dom's setProp
        // lists it with `case 'defaultValue': break;`, so it is never an attribute
        // anywhere. On a non-form element that means writing nothing at all -
        // which is still "handled", and is what stops the dead `defaultvalue=`.
        if (!isFormControl)
            return true;
        // React writes nothing when there is neither a value nor a defaultValue,
        // so a null defaultValue must not invent a value="" (BUG-RUNTIME-065).
        if (value == null)
            return true;
        // A <select>'s defaultValue selects an option; assigning
        // select.defaultValue does nothing at all.
        if (tag === 'SELECT')
            el.value = value;
        else
            el.defaultValue = value;
        return true;
    }
    // defaultChecked - reserved in exactly the same way.
    if (!isFormControl)
        return true;
    el.defaultChecked = !!value;
    return true;
}
